import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HandHelping, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const CATEGORY_LABELS = { intro: "Intro", hiring: "Hiring", advice: "Advice", customers: "Customers", other: "Other" };

export const AsksPanel = ({ companyId }) => {
  const [asks, setAsks] = useState([]);
  const [respondTo, setRespondTo] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/portal/companies/${companyId}/asks`); setAsks(data); }
    catch (e) { console.error("Failed to load asks", e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyId]);

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post(`/asks/${respondTo.id}/respond`, { message: message.trim() });
      toast.success("The founder has been notified");
      setRespondTo(null); setMessage("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  if (asks.length === 0) return null;

  return (
    <div data-testid="investor-asks-panel">
      <h2 className="mb-3 flex items-center gap-2 h-display text-lg font-semibold text-foreground"><HandHelping className="h-5 w-5 text-primary" /> How you can help</h2>
      <div className="space-y-2">
        {asks.map((a) => (
          <Card key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4" data-testid={`investor-ask-${a.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{a.title}</span>
                <Badge variant="secondary" className="font-normal">{CATEGORY_LABELS[a.category] || a.category}</Badge>
              </div>
              {a.description && <p className="mt-0.5 text-xs text-muted-foreground">{a.description}</p>}
            </div>
            {a.my_response ? (
              <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle2 className="mr-1 h-3 w-3" /> You offered to help</Badge>
            ) : (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setRespondTo(a)} data-testid={`investor-ask-help-${a.id}`}>
                <HandHelping className="h-3.5 w-3.5" /> I can help
              </Button>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={!!respondTo} onOpenChange={(v) => !v && setRespondTo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Help with "{respondTo?.title}"</DialogTitle></DialogHeader>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell the founder how you can help…" rows={4} data-testid="ask-help-message" />
          <Button onClick={send} disabled={sending || !message.trim()} className="w-full gap-2" data-testid="ask-help-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send to founder
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
